import { normalizeCornerRadiusMode, normalizeCornerRadiusValue } from '@lib/cornerRadius';
import { PRODUCT_CONTAINER_DISPLAY_AT_VALUES } from '@/options/displayAtOptions';
import {
	AppearanceConfig,
	BackgroundType,
	Category,
	ConfigData,
	DisplayAt,
	Product,
	SpecificPages,
	TextAlignment,
	TimerType,
} from '@/types';

export type AppearanceView = 'desktop' | 'mobile';

type LegacyStringAppearanceField = keyof Omit<
	AppearanceConfig,
	| 'text_alignment'
	| 'background_type'
	| 'background_image'
	| 'original_image_name'
	| 'timer_window_border_width'
	| 'timer_window_border_color'
	| 'timer_content_spacing_top'
	| 'timer_content_spacing_right'
	| 'timer_content_spacing_bottom'
	| 'timer_content_spacing_left'
	| 'timer_corner_radius_mode'
	| 'timer_corner_radius_all'
	| 'timer_corner_radius_top_left'
	| 'timer_corner_radius_top_right'
	| 'timer_corner_radius_bottom_left'
	| 'timer_corner_radius_bottom_right'
>;

const MAX_CONTENT_SPACING = 4294967295;

const STRING_FIELDS: LegacyStringAppearanceField[] = [
	'title_font_style',
	'subtitle_font_style',
	'button_text_color',
	'button_bg_color',
	'font',
	'background_color',
	'timer_window_color',
	'countdown_color',
	'time_units_color',
	'custom_css',
];

export function getDesktopAppearance(config: ConfigData): AppearanceConfig {
	const backgroundType = normalizeBackgroundType(config.background_type, config.background_image);

	return {
		text_alignment: config.text_alignment,
		title_font_style: config.title_font_style,
		subtitle_font_style: config.subtitle_font_style,
		button_text_color: config.button_text_color,
		button_bg_color: config.button_bg_color,
		font: config.font,
		background_type: backgroundType,
		background_image: backgroundType === BackgroundType.IMAGE ? config.background_image : null,
		original_image_name: backgroundType === BackgroundType.IMAGE ? config.original_image_name : null,
		background_color: config.background_color,
		timer_window_color: config.timer_window_color,
		timer_window_border_width: normalizeBorderWidth(config.timer_window_border_width),
		timer_window_border_color: getString(config.timer_window_border_color, '#000000'),
		timer_content_spacing_top: normalizeContentSpacing(config.timer_content_spacing_top),
		timer_content_spacing_right: normalizeContentSpacing(config.timer_content_spacing_right),
		timer_content_spacing_bottom: normalizeContentSpacing(config.timer_content_spacing_bottom),
		timer_content_spacing_left: normalizeContentSpacing(config.timer_content_spacing_left),
		timer_corner_radius_mode: normalizeCornerRadiusMode(config.timer_corner_radius_mode),
		timer_corner_radius_all: normalizeCornerRadiusValue(config.timer_corner_radius_all),
		timer_corner_radius_top_left: normalizeCornerRadiusValue(config.timer_corner_radius_top_left),
		timer_corner_radius_top_right: normalizeCornerRadiusValue(config.timer_corner_radius_top_right),
		timer_corner_radius_bottom_left: normalizeCornerRadiusValue(config.timer_corner_radius_bottom_left),
		timer_corner_radius_bottom_right: normalizeCornerRadiusValue(config.timer_corner_radius_bottom_right),
		countdown_color: config.countdown_color,
		time_units_color: config.time_units_color,
		custom_css: config.custom_css,
	};
}

export function getEffectiveAppearance(config: ConfigData, view: AppearanceView): AppearanceConfig {
	const desktopAppearance = getDesktopAppearance(config);

	if (view === 'desktop' || config.mobile_use_desktop_settings !== false) {
		return desktopAppearance;
	}

	return normalizeAppearanceConfig(config.mobile_appearance) ?? desktopAppearance;
}

export function buildMobileAppearanceSnapshot(config: ConfigData): AppearanceConfig {
	return getDesktopAppearance(config);
}

export function isLiquidGlassBackground(appearance: Pick<AppearanceConfig, 'background_type'>): boolean {
	return appearance.background_type === BackgroundType.LIQUID_GLASS;
}

export function usesLiquidGlassInAnyView(config: ConfigData): boolean {
	return (
		isLiquidGlassBackground(getEffectiveAppearance(config, 'desktop')) ||
		isLiquidGlassBackground(getEffectiveAppearance(config, 'mobile'))
	);
}

const DEFAULT_BACKGROUNDS_PATH_SEGMENT = '/public/default_backgrounds/';

/**
 * True when the merchant uploaded their own image, as opposed to picking one from the gallery or
 * inheriting a template default — both of those live under `/public/default_backgrounds/`, while an
 * upload is stored as `/public/backgrounds/<storeHash>/<hash><ext>`.
 */
export function isUploadedBackground(
	appearance: Pick<AppearanceConfig, 'background_type' | 'background_image'>,
): boolean {
	return (
		appearance.background_type === BackgroundType.IMAGE &&
		!!appearance.background_image &&
		!appearance.background_image.includes(DEFAULT_BACKGROUNDS_PATH_SEGMENT)
	);
}

export const LIQUID_GLASS_TEXT_COLOR = '#000000';

export type TextColorAppearance = Pick<
	AppearanceConfig,
	'title_font_style' | 'subtitle_font_style' | 'button_text_color' | 'countdown_color' | 'time_units_color'
>;

export function pickTextColors(appearance: TextColorAppearance): TextColorAppearance {
	return {
		title_font_style: appearance.title_font_style,
		subtitle_font_style: appearance.subtitle_font_style,
		button_text_color: appearance.button_text_color,
		countdown_color: appearance.countdown_color,
		time_units_color: appearance.time_units_color,
	};
}

export function buildLiquidGlassTextColors(appearance: TextColorAppearance): TextColorAppearance {
	return {
		title_font_style: withFontStyleColor(appearance.title_font_style, LIQUID_GLASS_TEXT_COLOR),
		subtitle_font_style: withFontStyleColor(appearance.subtitle_font_style, LIQUID_GLASS_TEXT_COLOR),
		button_text_color: withFontStyleColor(appearance.button_text_color, LIQUID_GLASS_TEXT_COLOR),
		countdown_color: LIQUID_GLASS_TEXT_COLOR,
		time_units_color: LIQUID_GLASS_TEXT_COLOR,
	};
}

function withFontStyleColor(fontStyle: string, color: string): string {
	const [fontSize = '16px', decoration = 'normal'] = fontStyle.split(' ');

	return `${fontSize} ${decoration} ${color}`;
}

export function normalizeConfigData(config: ConfigData): ConfigData {
	const backgroundType = normalizeBackgroundType(config.background_type, config.background_image);
	const mobileAppearance = normalizeAppearanceConfig(config.mobile_appearance);
	const timerType = normalizeTimerType(config.timer_type);

	return {
		...config,
		timer_type: timerType,
		template_id: normalizeTemplateId(config.template_id),
		display_at: normalizeDisplayAt(config.display_at, timerType),
		specificPages: normalizeSpecificPages(config.specificPages),
		categories: normalizeCategories(config.categories),
		products: normalizeProducts(config.products),
		background_type: backgroundType,
		background_image: backgroundType === BackgroundType.IMAGE ? config.background_image : null,
		original_image_name: backgroundType === BackgroundType.IMAGE ? config.original_image_name : null,
		mobile_appearance: mobileAppearance,
	};
}

export function normalizeAppearanceConfig(value: unknown): AppearanceConfig | null {
	if (!isRecord(value) || !isTextAlignment(value.text_alignment)) {
		return null;
	}

	for (const field of STRING_FIELDS) {
		if (typeof value[field] !== 'string') {
			return null;
		}
	}

	if (!isNullableString(value.background_image) || !isNullableString(value.original_image_name)) {
		return null;
	}

	const backgroundType = normalizeBackgroundType(value.background_type, value.background_image);

	return {
		text_alignment: value.text_alignment,
		title_font_style: value.title_font_style as string,
		subtitle_font_style: value.subtitle_font_style as string,
		button_text_color: value.button_text_color as string,
		button_bg_color: value.button_bg_color as string,
		font: value.font as string,
		background_type: backgroundType,
		background_image: backgroundType === BackgroundType.IMAGE ? value.background_image : null,
		original_image_name: backgroundType === BackgroundType.IMAGE ? value.original_image_name : null,
		background_color: value.background_color as string,
		timer_window_color: value.timer_window_color as string,
		timer_window_border_width: normalizeBorderWidth(value.timer_window_border_width),
		timer_window_border_color: getString(value.timer_window_border_color, '#000000'),
		timer_content_spacing_top: normalizeContentSpacing(value.timer_content_spacing_top),
		timer_content_spacing_right: normalizeContentSpacing(value.timer_content_spacing_right),
		timer_content_spacing_bottom: normalizeContentSpacing(value.timer_content_spacing_bottom),
		timer_content_spacing_left: normalizeContentSpacing(value.timer_content_spacing_left),
		timer_corner_radius_mode: normalizeCornerRadiusMode(value.timer_corner_radius_mode),
		timer_corner_radius_all: normalizeCornerRadiusValue(value.timer_corner_radius_all),
		timer_corner_radius_top_left: normalizeCornerRadiusValue(value.timer_corner_radius_top_left),
		timer_corner_radius_top_right: normalizeCornerRadiusValue(value.timer_corner_radius_top_right),
		timer_corner_radius_bottom_left: normalizeCornerRadiusValue(value.timer_corner_radius_bottom_left),
		timer_corner_radius_bottom_right: normalizeCornerRadiusValue(value.timer_corner_radius_bottom_right),
		countdown_color: value.countdown_color as string,
		time_units_color: value.time_units_color as string,
		custom_css: value.custom_css as string,
	};
}

export function normalizeBackgroundType(value: unknown, backgroundImage: unknown): BackgroundType {
	const hasImage = typeof backgroundImage === 'string' && backgroundImage.length > 0;

	if (value === BackgroundType.LIQUID_GLASS || value === BackgroundType.COLOR) {
		return value;
	}

	return hasImage ? BackgroundType.IMAGE : BackgroundType.COLOR;
}

function isRecord(value: unknown): value is Partial<Record<keyof AppearanceConfig, unknown>> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeTimerType(value: unknown): TimerType {
	return value === TimerType.PRODUCT_CONTAINER ? TimerType.PRODUCT_CONTAINER : TimerType.BAR;
}

function normalizeDisplayAt(value: unknown, timerType: TimerType): DisplayAt {
	const displayAt = Object.values(DisplayAt).includes(value as DisplayAt) ? (value as DisplayAt) : DisplayAt.HOME;

	if (timerType === TimerType.PRODUCT_CONTAINER && !PRODUCT_CONTAINER_DISPLAY_AT_VALUES.includes(displayAt)) {
		return DisplayAt.PRODUCT;
	}

	return displayAt;
}

function normalizeTemplateId(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizeSpecificPages(value: unknown): SpecificPages[] {
	if (!Array.isArray(value)) {
		return [];
	}

	return value.reduce<SpecificPages[]>((pages, item) => {
		if (!isUnknownRecord(item) || typeof item.url !== 'string' || item.url.length === 0) {
			return pages;
		}

		const id = normalizePositiveInteger(item.id);
		pages.push(id ? { id, url: item.url } : { url: item.url });

		return pages;
	}, []);
}

function normalizeCategories(value: unknown): Category[] {
	if (!Array.isArray(value)) {
		return [];
	}

	return value.reduce<Category[]>((categories, item) => {
		if (!isUnknownRecord(item)) {
			return categories;
		}

		const externalId = normalizePositiveInteger(item.externalId);

		if (!externalId) {
			return categories;
		}

		const id = normalizePositiveInteger(item.id);
		const category = {
			externalId,
			name: getString(item.name, `Category ${externalId}`),
			categoryUrl: typeof item.categoryUrl === 'string' ? item.categoryUrl : '',
		};

		categories.push(id ? { id, ...category } : category);

		return categories;
	}, []);
}

function normalizeProducts(value: unknown): Product[] {
	if (!Array.isArray(value)) {
		return [];
	}

	return value.reduce<Product[]>((products, item) => {
		if (!isUnknownRecord(item)) {
			return products;
		}

		const externalId = normalizePositiveInteger(item.externalId);

		if (!externalId) {
			return products;
		}

		products.push({
			externalId,
			name: getString(item.name, `Product ${externalId}`),
			productUrl: getNullableString(item.productUrl),
			imageUrl: getNullableString(item.imageUrl),
		});

		return products;
	}, []);
}

function isTextAlignment(value: unknown): value is TextAlignment {
	return Object.values(TextAlignment).includes(value as TextAlignment);
}

function isNullableString(value: unknown): value is string | null {
	return value === null || typeof value === 'string';
}

function getNullableString(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizePositiveInteger(value: unknown): number | null {
	return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function normalizeBorderWidth(value: unknown, fallback = 0): number {
	return typeof value === 'number' && Number.isInteger(value) ? Math.min(5, Math.max(0, value)) : fallback;
}

function normalizeContentSpacing(value: unknown, fallback = 0): number {
	return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_CONTENT_SPACING
		? value
		: fallback;
}

function getString(value: unknown, fallback: string): string {
	return typeof value === 'string' && value.length > 0 ? value : fallback;
}
